/**
 * Template placeholders left in notice title/body before publish.
 *
 * Supported forms:
 * - `{% type: Name %}` typed Blanks (see domain/notices/templateBlanks)
 * - `{% variableName %}` (older Templates / premade notices)
 * - `{variableName}` (simple brace placeholders in docs / paste)
 *
 * Deliberately looser than the Blank syntax: anything inside `{% … %}` counts,
 * so no unfilled Blank can get past the send check.
 */
import { TEMPLATE_BLANK_TYPE_PATTERN } from "../domain/notices/templateBlankTypes";

const TEMPLATE_PLACEHOLDER_RE = new RegExp(
  `\\{%\\s*(?:(?:${TEMPLATE_BLANK_TYPE_PATTERN})\\s*:\\s*)?((?:[^{}%]|%(?!\\}))*?)\\s*%\\}`,
  "gi",
);
const SIMPLE_PLACEHOLDER_RE = /\{([A-Za-z_][\w]*)\}/g;

export type NoticePlaceholderMatch = {
  fullMatch: string;
  variableName: string;
  startIndex: number;
  endIndex: number;
};

function collectMatches(
  text: string,
  regex: RegExp,
): NoticePlaceholderMatch[] {
  const matches: NoticePlaceholderMatch[] = [];
  const re = new RegExp(regex.source, regex.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    matches.push({
      fullMatch: match[0],
      variableName: match[1],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }
  return matches;
}

/** All placeholder spans in text, ordered by position (no overlaps). */
export function findNoticePlaceholders(
  text: string,
): NoticePlaceholderMatch[] {
  if (!text) return [];

  const templateMatches = collectMatches(text, TEMPLATE_PLACEHOLDER_RE);
  const occupied = new Set<number>();
  for (const m of templateMatches) {
    for (let i = m.startIndex; i < m.endIndex; i += 1) occupied.add(i);
  }

  const simpleMatches = collectMatches(text, SIMPLE_PLACEHOLDER_RE).filter(
    (m) => {
      // Skip `{% name %}` inner braces already covered by the template form.
      for (let i = m.startIndex; i < m.endIndex; i += 1) {
        if (occupied.has(i)) return false;
      }
      return true;
    },
  );

  return [...templateMatches, ...simpleMatches].sort(
    (a, b) => a.startIndex - b.startIndex,
  );
}

export function hasPlaceholders(text: string): boolean {
  return findNoticePlaceholders(text || "").length > 0;
}

/** Full match strings for error messages (e.g. `{% date %}`, `{parent}`). */
export function extractPlaceholders(text: string): string[] {
  return findNoticePlaceholders(text || "").map((m) => m.fullMatch);
}

export function placeholdersErrorMessage(text: string): string {
  const placeholders = extractPlaceholders(text);
  if (placeholders.length === 0) return "";
  return `Please fill in the value${placeholders.length > 1 ? "s" : ""}: ${placeholders.join(", ")}`;
}
